import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'

type FormActionBarProps = {
  /** react-hook-form's `formState.isDirty`. Drives the whole bar. */
  isDirty: boolean
  isSubmitting?: boolean
  /** Usually the form's own `reset()` — back to what the server last said. */
  onDiscard?: () => void
  /** Anything this form needs beside the save, e.g. a destructive action. */
  children?: ReactNode
  className?: string
}

/**
 * The save, and where it lives now.
 *
 * A form's save used to sit in a `flex justify-end` at the very bottom, which
 * on a page of settings means below everything and out of sight — the reader
 * scrolls to look for it, and its position tells them nothing about whether
 * there is anything to save. This is the same single save, floating where it is
 * always reachable and only while there is something to do with it.
 *
 * **Deviation from the design system.** Composition rule 7 says the save lives
 * in the form's footer and not floating in a corner. The objection there is to
 * *two* saves — "two different promises about what gets saved" — and this
 * replaces the footer rather than joining it, so the promise stays single. The
 * rule is amended to match; see the Composition section.
 *
 * It is the same capsule as the dock and the topbar pill, in the opposite
 * corner: same glass, same border, same 20 radius, same elevation. The first
 * version of this was a sticky slab pulled out to the column's edges, and on a
 * page whose form is one card among several it read as a grey band wedged
 * between two cards rather than as chrome — because a full-width bar in the
 * flow is page furniture, and this is not part of the page.
 *
 * Mounted only while the form is dirty. It is `fixed`, so appearing costs no
 * layout shift — the reservation the sticky version needed was a consequence of
 * sitting in the flow, and nothing is left to reserve.
 */
export function FormActionBar({
  isDirty,
  isSubmitting = false,
  onDiscard,
  children,
  className,
}: FormActionBarProps) {
  const { t } = useTranslation('common')

  // Kept through the submit: unmounting the bar the instant the form is no
  // longer dirty would take the spinner away mid-request.
  if (!isDirty && !isSubmitting) return null

  return (
    <div
      data-slot="form-action-bar"
      // Announced when it appears, not read out as a live region on every
      // keystroke — the words below are the same words every time.
      role="status"
      className={cn(
        // 56 tall, which is what the dock capsule and the topbar pill measure:
        // `py-2.5` around a `h-control-md` control is their `py-2` around a
        // 40px tile. Three pieces of chrome on one screen, one silhouette.
        'fixed right-[15px] bottom-[15px] z-40 flex items-center gap-2 rounded-[20px] border border-border bg-surface-1 px-2.5 py-2.5 backdrop-blur-[20px] shadow-dock animate-rise',
        // Below `sm` the mobile dock floats at `bottom-4` and is 60-odd tall,
        // so the bar clears it and spans the width instead of crowding a corner.
        'max-sm:inset-x-4 max-sm:bottom-24 max-sm:justify-end',
        className,
      )}
    >
      {/* The reason the bar is here at all. Hidden on the narrowest screens,
          where the two controls have to be the whole width. */}
      <span className="ml-1.5 truncate text-caption text-fg-secondary max-sm:hidden">
        {t('form.unsaved')}
      </span>

      {children}

      {onDiscard !== undefined && (
        <Button type="button" variant="ghost" onClick={onDiscard}>
          {t('actions.discard')}
        </Button>
      )}

      <Button type="submit" variant="default" isLoading={isSubmitting}>
        {t('actions.save')}
      </Button>
    </div>
  )
}
